/**
 * Function to display Review tab
 * Modified on 10-01-2025
 */
//  Generic imports
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

//  Motif controls
import {
  MotifCard,
  MotifCardBody,
  MotifCardHeader,
  MotifButton,
  MotifTooltip,
} from "@ey-xd/motif-react";
import { MotifIconButton } from "@ey-xd/motif-react/Button";
import MotifToast from "@ey-xd/motif-react/Toast";

//  Internal imports
import "./Review.scss";
import DiscardTransfer from "../../molecules/modals/discardTransfer/DiscardTransfer";
import ReviewService from "../../../services/reviewService";
import { reviewConstants } from "../../../assets/data/constants";
import {
  setReviewFeatureResults,
  setActiveReviewFeatureResult,
} from "../../../redux/userSlice";
import { setShowToast } from "../../../redux/toastSlice";

//  Icons imports
import iconArrowRight from "../../../assets/images/arrow-right-black.svg";
import iconArrowBack from "../../../assets/images/arrow_back.svg";
import iconPageSearch from "../../../assets/images/page-search.svg";
import iconPageSearchWhite from "../../../assets/images/page-search-white.svg";
import iconLoader from "../../../assets/images/Loading_indicator_circles.svg";
import iconTrash from "../../../assets/images/Icontrash.svg";
import iconRefresh from "../../../assets/images/refresh.svg";
import iconInfoEmpty from "../../../assets/images/info-empty.svg";
import iconArrowBackWhite from "../../../assets/images/Iconarrow.svg";
import iconCheckCircle from "../../../assets/images/check_circle.svg";
import iconWarningCircle from "../../../assets/images/warning_circle.svg";

/* global Office */

const Review = () => {
  //  hooks
  const dispatch = useDispatch();
  const { activeProject } = useSelector((state) => state.user);

  //  Use state hooks
  const [isFeatureSelected, setIsFeatureSelected] = useState(false);
  const [featureResponseId, setFeatureResponseId] = useState(null);
  const [featureResults, setFeatureResults] = useState([]);
  const [selectedFeatureResult, setSelectedFeatureResult] = useState(null);
  const [showNoTempWordFound, setShowNoTempWordFound] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [discardModalShow, setDiscardModalShow] = useState(false);
  const [showDiscardToast, setShowDiscardToast] = useState(false);
  const [isInsertDisabled, setIsInsertDisabled] = useState(true);
  const [showToastMessage, setShowToastMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isDiscardAllClicked, setIsDiscardAllClicked] = useState(false);

  const reviewService = new ReviewService();
  const projectId = activeProject?.project_UUID;

  const handleFeatureSelection = () => {
    setIsFeatureSelected(true);
  };

  const handleBackClick = () => {
    setIsFeatureSelected(false);
    if (featureResults.length) {
      setFeatureResponseId(null);
      setFeatureResults([]);
      dispatch(
        setReviewFeatureResults({
          reviewResponseId: null,
          reviewFeatureResults: [],
        })
      );
    }
    setSelectedFeatureResult(null);
    dispatch(
      setActiveReviewFeatureResult({
        setActiveReviewFeatureResult: null,
      })
    );
  };

  /**
   * Function to generate review template wordings
   */
  const handleGenerateFeatureResults = () => {
    setShowLoader(true);
    if (
      window.Office &&
      window.Office.context &&
      window.Office.context.host === "PowerPoint"
    ) {
      Office.context.document.getSelectedDataAsync(
        Office.CoercionType.SlideRange,
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            const slides = result.value.slides;
            if (slides.length > 0) {
              const slide = slides[0];
              if (slide?.title) {
                generateFeatureResults(slide.title);
              } else {
                //  No title found on this slide.
                setShowNoTempWordFound(true);
                setShowLoader(false);
              }
            } else {
              //  No slides selected.
              setShowNoTempWordFound(true);
              setShowLoader(false);
            }
          } else {
            //  "Error: " + result.error.message
            setShowNoTempWordFound(true);
            setShowLoader(false);
          }
        }
      );
    } else {
      //  Please open this in PowerPoint"
      setShowNoTempWordFound(true);
      setShowLoader(false);
    }
  };

  /**
   * Function to generate slide template wordings for current slide data
   */
  const generateFeatureResults = async (vSlideTitle) => {
    try {
      await window.PowerPoint.run(async (context) => {
        const vCurrentSlideContent = [];
        const selectedSlides = context.presentation.getSelectedSlides();
        selectedSlides.load("items");
        await context.sync();
        if (selectedSlides.items.length === 0) {
          setShowNoTempWordFound(true);
          setShowLoader(false);
          return;
        }
        const currentSlide = selectedSlides.items[0];
        currentSlide.shapes.load("items");
        await context.sync();
        currentSlide.shapes.items.forEach((shape) => {
          if (
            (shape.type === "Placeholder" ||
              shape.type === "TextBox" ||
              shape.type === "GeometricShape") &&
            shape.textFrame.textRange
          ) {
            shape.textFrame.load("textRange/text");
          }
        });
        await context.sync();
        await currentSlide.shapes.items.map(async (shape) => {
          let vText = "";
          if (
            (shape.type === "Placeholder" ||
              shape.type === "TextBox" ||
              shape.type === "GeometricShape") &&
            shape.textFrame.textRange.text
          ) {
            vText = shape.textFrame.textRange.text;
          }
          vCurrentSlideContent.push({
            id: shape.id,
            type: shape.type,
            name: shape.name,
            text: vText,
          });
        });
        const varData = {
          reviewsRequest: {
            projectId: projectId,
            title: vSlideTitle,
            req: vCurrentSlideContent,
          },
        };
        let vFeatureResults = {
          responseId: null,
          responses: []
        }
        const response = await reviewService.getReview(varData);
        if (response?.responses?.length) {
          vFeatureResults.responseId = response.responseId;
          vFeatureResults.responses = response.responses;
        } else {
          setShowNoTempWordFound(true);
        }
        setFeatureResponseId(vFeatureResults.responseId);
        setFeatureResults(vFeatureResults.responses);
        dispatch(
          setReviewFeatureResults({
            reviewResponseId: vFeatureResults.responseId,
            reviewFeatureResults: vFeatureResults.responses,
          })
        );
        setShowLoader(false);
      });
    } catch (error) {
      //  Error retrieving selected slide data
      setShowNoTempWordFound(true);
      setShowLoader(false);
    }
  };

  const onSelectReviewFeatureResult = (id) => {
    setSelectedFeatureResult(id);
    dispatch(
      setActiveReviewFeatureResult({
        selectedFeatureResult: id,
      })
    );
  };

  /**
   * Function to handle discard all
   */
  const handleDiscardAll = () => {
    setIsDiscardAllClicked(true);
    setDiscardModalShow(true);
  };

  /**
   * Function to discard all
   */
  const discardAll = async () => {
    if (featureResults.length) {
      for (const element of featureResults) {
        const vResponse = { ...element, operation: "discard" };
        const vData = {
          "ResponseId": featureResponseId,
          "Responses": []
        };
        vData.Responses.push(vResponse);
        await reviewService.updateReview(vData);
      }
      setFeatureResponseId(null);
      setFeatureResults([]);
      dispatch(
        setReviewFeatureResults({
          reviewResponseId: null,
          reviewFeatureResults: [],
        })
      );
    }
    setDiscardModalShow(false);
    setShowDiscardToast(true);
  };

  const handleBackFeatureResults = () => {
    setSelectedFeatureResult(null);
    dispatch(
      setActiveReviewFeatureResult({
        setActiveReviewFeatureResult: null,
      })
    );
  };

  /**
   * handle discard feature
   */
  const handleDiscard = () => {
    setIsDiscardAllClicked(false);
    setDiscardModalShow(true);
  };

  const discard = async () => {
    const vSelectedFeatureResult = featureResults[selectedFeatureResult];
    const vResponse = { ...vSelectedFeatureResult, operation: "discard" };
    const vData = {
      "ResponseId": featureResponseId,
      "Responses": []
    };
    vData.Responses.push(vResponse);
    const response = await reviewService.updateReview(vData);
    if (response) {
      removeSelectedFeature();
      handleBackFeatureResults();
      setDiscardModalShow(false);
      setShowDiscardToast(true);
    }
  };

  const removeSelectedFeature = () => {
    const vFilteredFeatureResults = featureResults.filter(
      (element, index) => index.toString() !== selectedFeatureResult.toString()
    );
    setFeatureResults(vFilteredFeatureResults);
    dispatch(
      setReviewFeatureResults({
        reviewResponseId: featureResponseId,
        reviewFeatureResults: vFilteredFeatureResults,
      })
    );
  };

  /**
   * Hook to add document selection change handler
   */
  useEffect(() => {
    if (Office.context.document) {
      Office.context.document.addHandlerAsync(
        "documentSelectionChanged",
        handleInsertVisibility,
        (res) => {}
      );
    }
    //  eslint-disable-next-line
  }, []);

  /**
   * Function to check whether placed cursor in PPT or selected any text
   * to enable or disable insert button
   */
  const handleInsertVisibility = () => {
    try {
      Office.context.document.getSelectedDataAsync(
        Office.CoercionType.Text,
        (res) => {
          if (res.status === Office.AsyncResultStatus.Failed) {
            setIsInsertDisabled(true);
          } else {
            setIsInsertDisabled(false);
          }
        }
      );
    } catch (error) {}
  };

  const insertFeatureToPowerpoint = async () => {
    setIsInsertDisabled(true);
    if (selectedFeatureResult !== null) {
      try {
        const vSelectedFeatureResult = featureResults[selectedFeatureResult];
        await Office.context.document.setSelectedDataAsync(
          vSelectedFeatureResult?.template,
          async (res) => {
            if (res.status === Office.AsyncResultStatus.Failed) {
              dispatch(
                setShowToast({
                  showToast: true,
                  toastVariant: "error",
                })
              );
              setIsInsertDisabled(false);
              return;
            } else {
              const vResponse = { ...vSelectedFeatureResult, operation: "insert" };
              const vData = {
                "ResponseId": featureResponseId,
                "Responses": []
              };
              vData.Responses.push(vResponse);
              const response = await reviewService.updateReview(vData);
              if (response) {
                handleBackFeatureResults();
                removeSelectedFeature();
                setSuccessMessage("Template Wording inserted successfully");
                setShowToastMessage(true);
              }
            }
          }
        );
      } catch (error) {
        dispatch(
          setShowToast({
            showToast: true,
            toastVariant: "error",
          })
        );
      }
    }
    setIsInsertDisabled(false);
  };

  const closeDiscardModal = () => {
    setDiscardModalShow(false);
  };

  const confirmDiscard = () => {
    if (isDiscardAllClicked) {
      discardAll();
    } else {
      discard();
    }
  };

  return (
    <div className="reviewContainer">
      {!showLoader && !isFeatureSelected && (
        <div className="templateWording">
          <MotifCard
            className="template-word-card"
            onClick={() => handleFeatureSelection()}
          >
            <div className="w-50">
              <MotifCardHeader variant="alt">
                <span className="featureTitle">
                  {reviewConstants?.featureTitle}
                </span>
              </MotifCardHeader>
              <MotifCardBody>
                <span className="featureSubTitle">
                  {reviewConstants?.featureSubTitle}
                </span>
              </MotifCardBody>
            </div>
            <MotifIconButton className="iconArrowRight">
              <img src={iconArrowRight} alt="Right Arrow" />
            </MotifIconButton>
          </MotifCard>
        </div>
      )}
      {isFeatureSelected && (
        <div className="reviewCard expandedview">
          <MotifCard className="expanded-card">
            <MotifCardHeader className="expanded-header">
              <div className="arrowText">
                {
                  (selectedFeatureResult === null) &&
                  <MotifIconButton
                    aria-label="Click here to go back"
                    onClick={handleBackClick}
                    className="btnBack"
                  >
                    <img src={iconArrowBack} alt="Previous" />
                  </MotifIconButton>
                }
                <span className="backButtonText">
                  {" "}
                  {reviewConstants?.featureTitle}
                </span>
              </div>
            </MotifCardHeader>
            {showLoader && (
              <div className="loaderDiv">
                <div>
                  <img
                    src={iconLoader}
                    alt="Progress"
                    className="refreshIcon "
                  />
                </div>
                <div className="refreshListText">
                  {reviewConstants?.loadingMessage}
                </div>
              </div>
            )}
            {!showLoader && featureResults.length === 0 && (
              <div className="no-info-layer">
                <img
                  src={iconPageSearch}
                  alt="Search"
                  className="iconPageSearch"
                />
                <span className="no-info-title">
                  {reviewConstants?.noResultsTitle}
                </span>
                <span className="no-info-subtitle">
                  {reviewConstants?.noResultsSubTitle}
                </span>
              </div>
            )}
            {!showLoader &&
              selectedFeatureResult === null &&
              featureResults.length > 0 && (
                <div className="reviewResponseLayer">
                  {featureResults.map((element, elemIndex) => (
                    <MotifCard
                      className="reviewResponseCard"
                      onClick={() => onSelectReviewFeatureResult(elemIndex)}
                      key={element.title}
                    >
                      <MotifCardHeader variant="alt">
                        <span className="reviewResponseTitle">
                          {element?.title}
                        </span>
                      </MotifCardHeader>
                      <MotifCardBody>
                        <span className="reviewResponseSubTitle">
                          {element?.template}
                        </span>
                      </MotifCardBody>
                    </MotifCard>
                  ))}
                </div>
              )}
            {!showLoader &&
              selectedFeatureResult !== null &&
              featureResults.length > 0 && (
                <div className="featureResultCard">
                  <MotifCard className="expanded-card">
                    <MotifCardHeader className="expanded-review-result-header">
                      <div className="arrowText">
                        <MotifIconButton
                          aria-label="Click here to go back"
                          onClick={() => handleBackFeatureResults()}
                          className="btnBack"
                        >
                          <img src={iconArrowBack} alt="Previous" />
                        </MotifIconButton>
                        <span className="backButtonText"> Back</span>
                      </div>
                      <span className="expanded-title">
                        {featureResults[selectedFeatureResult].title}
                      </span>
                    </MotifCardHeader>
                    <div className="expanded-content">
                      {featureResults[selectedFeatureResult].template}
                    </div>
                  </MotifCard>
                </div>
              )}
          </MotifCard>
        </div>
      )}
      {isFeatureSelected && (
        <div className="genericFooter">
          <div className="footerInner alignEnd">
            {featureResults.length === 0 && (
              <MotifButton
                className="btnIdentify"
                onClick={handleGenerateFeatureResults}
                disabled={showLoader}
              >
                <img src={iconPageSearchWhite} alt="Search" />
                <span className="txtIdentify">
                  {reviewConstants?.btnIdentifyLabel}
                </span>
              </MotifButton>
            )}
            {!showLoader &&
              selectedFeatureResult === null &&
              featureResults.length > 0 && (
                <>
                  <MotifButton
                    className="btnDiscardAll"
                    onClick={() => handleDiscardAll()}
                    variant="secondary"
                  >
                    <img
                      src={iconTrash}
                      alt="Discard All"
                      className="iconTrash"
                    />
                    <span className="txtTrash">
                      {reviewConstants?.btnDiscardAllLabel}
                    </span>
                  </MotifButton>
                  <MotifButton
                    className="btnRegenerate"
                    onClick={() => handleGenerateFeatureResults()}
                    variant="secondary"
                  >
                    <img
                      src={iconRefresh}
                      alt="Regenerate"
                      className="iconRegenerate"
                    />
                    <span className="txtRegenerate">
                      {reviewConstants?.btnRefreshLabel}
                    </span>
                  </MotifButton>
                </>
              )}
            {!showLoader &&
              selectedFeatureResult !== null &&
              featureResults.length > 0 && (
                <>
                  <MotifTooltip
                    hide={!isInsertDisabled}
                    trigger={
                      <MotifButton
                        className="btnRegenerate"
                        variant="primary"
                        disabled={isInsertDisabled}
                        onClick={insertFeatureToPowerpoint}
                      >
                        <img
                          src={iconArrowBackWhite}
                          alt="Insert"
                          className="iconRegenerate"
                        />
                        <span className="txtRegenerate">
                          {reviewConstants?.btnInsertLabel}
                        </span>
                      </MotifButton>
                    }
                    contentClassName="tooltipWithoutClose"
                    allowHover={true}
                    allowClick={false}
                    hideCloseButton={true}
                  >
                    Select within a text
                    <br />
                    box to continue
                  </MotifTooltip>
                  <MotifButton
                    className="btnDiscardAll"
                    onClick={() => handleDiscard()}
                    variant="secondary"
                  >
                    <img src={iconTrash} alt="Discard" className="iconTrash" />
                    <span className="txtTrash">
                      {reviewConstants?.btnDiscardLabel}
                    </span>
                  </MotifButton>
                </>
              )}
          </div>
        </div>
      )}
      {discardModalShow && (
        <div className="discard-modal-container">
          <DiscardTransfer
            discardModalShow={discardModalShow}
            closeDiscardModal={() => closeDiscardModal()}
            discardResponseTransfer={() => confirmDiscard()}
            discardTitle={
              isDiscardAllClicked
                ? reviewConstants?.discardAllTitle
                : reviewConstants?.discardTitle
            }
            discardMessage={
              isDiscardAllClicked
                ? reviewConstants?.discardAllMessage
                : reviewConstants?.discardMessage
            }
          />
        </div>
      )}
      {showToastMessage && (
        <div className="toast-div">
          <MotifToast
            onClose={() => setShowToastMessage(false)}
            icons={{ toast: <img src={iconCheckCircle} alt="Success" /> }}
            variant="success"
          >
            {successMessage}
          </MotifToast>
        </div>
      )}
      {showNoTempWordFound && (
        <div className="toast-div">
          <MotifToast
            onClose={() => setShowNoTempWordFound(false)}
            icons={{ toast: <img src={iconWarningCircle} alt="Failure" /> }}
            variant="error"
          >
            {reviewConstants?.noTempWordFound}
          </MotifToast>
        </div>
      )}
      {showDiscardToast && (
        <div className="toast-div">
          <MotifToast
            onClose={() => setShowDiscardToast(false)}
            icons={{ toast: <img src={iconInfoEmpty} alt="Info" /> }}
          >
            <span className="bold-toast-message">
              {isDiscardAllClicked
                ? reviewConstants?.discardAllToastMessage
                : reviewConstants?.discardToastMessage}
            </span>
          </MotifToast>
        </div>
      )}
    </div>
  );
};

export default Review;
